import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'libs/styled';
import Text from 'components/Text';
import { ArrowIcon } from 'assets/media/icons/ArrowIcon';
import Button from 'components/Button';
import Link from '@sportson/core-web/components/Link';
import { above } from '@sportson/core-web/utils/mediaqueries';
import decodeHTMLEntities from '@sportson/core-web/utils/decodeHTMLEntities';
import TextButton from 'components/TextButton';
import { type WordpressACFWysiwyg } from 'libs/wordpress/types/acf/fields';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { gridMargin } from 'components/wrappers/ContentGridWrapper';
import Breakpoint from '@sportson/core-web/components/Breakpoint';
import BoxButton from 'components/BoxButton';
import MenuIcon from 'assets/media/icons/Menu';
import useAppDispatch from '@sportson/core-web/hooks/useAppDispatch';
import { show as showOverlay } from '@sportson/core-web/state/application/overlay';
import { type ButtonTheme } from 'config/branding/buttons';
import { type ICMSIcon } from 'components/Header/navigation/TopNav';
import SubCategories, { type SubCategory } from 'components/SubCategories/SubCategories';

const Div = styled('div')``;

const ParentLink = styled(Link)`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CategoryHeaderWrapper = styled('div')`
    padding: 24px 12px 16px 12px;

    ${above['desktop.sm']} {
        padding: 26px 40px 16px 40px;
    }
`;

const CategoryHeroTextArea = styled('div')`
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 18px;
    position: relative;
    padding: 24px 0 40px 0;

    ${above['desktop.sm']} {
        padding: 24px 0 50px 0;
    }
`;

const CategoryHeroNavigation = styled('div')`
    display: flex;
    position: relative;

    ${above['desktop.sm']} {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 24px;
    }
`;

const Wrapper = styled('div')``;

const StyledBoxButton = styled(BoxButton)`
    width: calc(100% - 12px);
`;

const StyledBoxButtonWrapper = styled('div')`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
`;

const Gradient = styled('div')`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 12px;
    pointer-events: none;
    z-index: 2;
    background: linear-gradient(to right, var(--color-base-white), rgba(255, 255, 255, 0));
`;

const DescriptionWrapper = styled('div')`
    grid-column: 1 / -1;

    ${above['desktop.sm']} {
        grid-column: 9 / span 4;
    }
`;

const DescriptionLimiter = styled('div')`
    // limit rows
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ descriptionVisible }) => (descriptionVisible ? 0 : 3)};
    margin-bottom: 16px;
`;

const StyledSizeGuideLinkWrapper = styled('div')`
    display: none;

    ${above['desktop.sm']} {
        display: flex;
        grid-column: 9 / span 4;
        align-self: flex-end;
        margin-left: auto;
    }
`;

const Icon = styled('img')`
    width: 16px;
    height: 16px;
`;

interface Icon {
    alt: string;
    url: string;
}

interface Button {
    link: string;
    label: string;
    buttonTheme: ButtonTheme;
}

interface CategoryHeaderLink {
    icon: ICMSIcon;
    button: Button;
}

export type CategoryHeaderProps = {
    heading?: string;
    description?: string | WordpressACFWysiwyg;
    parentCategory?: {
        id: number;
        title: string;
        permalink: string;
    };
    subCategories?: SubCategory[];
    sizeGuideButtons?: CategoryHeaderLink[];
};

const isTextClamped = (elm) => elm.scrollHeight > elm.clientHeight;

const CategoryHeader = ({
    heading,
    description,
    parentCategory,
    subCategories,
    sizeGuideButtons = [],
}: CategoryHeaderProps) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const [descriptionVisible, setDescriptionVisible] = useState(false);
    const [truncatedText, setTruncatedText] = useState(false);

    // Ensure sizeGuideButtons is always an array to prevent crashes
    const safeSizeGuideButtons = Array.isArray(sizeGuideButtons) ? sizeGuideButtons : [];

    useEffect(() => {
        function checkTruncated() {
            setTruncatedText(isTextClamped(ref.current));
        }
        if (description) {
            checkTruncated();
        }
    }, [description]);

    const displayCategories = () => {
        dispatch(
            showOverlay({
                view: 'categoriesOverlay',
                transition: 'from-left',
                data: { subCategories },
            }),
        );
    };

    return (
        <CategoryHeaderWrapper>
            <CategoryHeroTextArea>
                {heading && (
                    <Div gridColumn="1 / span 8" display="flex" flexDirection="column" gap="16px">
                        <Text
                            as="h1"
                            typography={[
                                'Content/32_120_0_500',
                                null,
                                'Content/40_120_0_500',
                                'Content/80_120_0_500',
                                null,
                                null,
                            ]}
                        >
                            {decodeHTMLEntities(heading)}
                        </Text>
                        {parentCategory && (
                            <ParentLink to={parentCategory?.permalink}>
                                <ArrowIcon direction="left" size="xsmall" />
                                <Text as="span" typography="UI/16_100_0_450">
                                    {t('category.back_to_all_X', { category: parentCategory?.title })}
                                </Text>
                            </ParentLink>
                        )}
                    </Div>
                )}
                {description && (
                    <DescriptionWrapper gridColumn="1 / span 8">
                        <DescriptionLimiter ref={ref} descriptionVisible={descriptionVisible}>
                            <Wysiwyg
                                data={description}
                                TextComponent={(props) => <Text typography="Content/20_140_0_350" {...props} />}
                            />
                        </DescriptionLimiter>
                        {truncatedText && (
                            <TextButton onClick={() => setDescriptionVisible(!descriptionVisible)}>
                                <Text as="span" typography="UI/16_100_0_450" mr="8px">
                                    {t('read_more')}
                                </Text>
                                <ArrowIcon size="xsmall" direction={descriptionVisible ? 'up' : 'down'} />
                            </TextButton>
                        )}
                    </DescriptionWrapper>
                )}
            </CategoryHeroTextArea>
            <CategoryHeroNavigation>
                {subCategories && subCategories.length > 0 && (
                    <Breakpoint
                        to="desktop.sm"
                        render={
                            <Wrapper>
                                <StyledBoxButtonWrapper>
                                    <StyledBoxButton size="large" onClick={() => displayCategories()}>
                                        <MenuIcon width="24px" />
                                        <Text>{t('categories')}</Text>
                                    </StyledBoxButton>
                                    <Gradient />
                                </StyledBoxButtonWrapper>
                            </Wrapper>
                        }
                    />
                )}
                {subCategories && <SubCategories subCategories={subCategories} />}
                <StyledSizeGuideLinkWrapper>
                    {safeSizeGuideButtons.map(({ button, icon }) => (
                        <Button key={button.link} url={button.link} aria-label={button.label} mb="14px">
                            <Icon src={icon.url} alt={icon.alt} />
                            <Text>{button.label}</Text>
                        </Button>
                    ))}
                </StyledSizeGuideLinkWrapper>
            </CategoryHeroNavigation>
        </CategoryHeaderWrapper>
    );
};

export default CategoryHeader;
